import React, { useState, useMemo, useEffect, useRef } from 'react'

import {
  Row,
  Col,
  OverlayTrigger,
  Popover,
  FormGroup,
  FormLabel,
} from 'react-bootstrap'

import { styled } from '@mui/material/styles'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import { makeStyles } from '@material-ui/core/styles'

import LocalizationProvider from '@mui/lab/LocalizationProvider'
import thLocale from 'date-fns/locale/th'
import OverwriteFnsBE from './OverwriteFnsBE'

import InputMask from 'react-input-mask'
import classNames from 'classnames/bind'

import StaticDateRangePicker from '@mui/lab/StaticDateRangePicker'
import MuiDateRangePickerDay from '@mui/lab/DateRangePickerDay'
import './datepicker-custom-styles.css'

import IconButton from '@mui/material/IconButton'
import TodayIcon from '@mui/icons-material/Today'
import CalendarPicker from '@mui/lab/CalendarPicker'

import {
  addDays,
  endOfDay,
  startOfDay,
  startOfMonth,
  endOfMonth,
  addMonths,
  startOfWeek,
  endOfWeek,
  startOfYear,
  endOfYear,
  addYears,
  getYear,
  startOfQuarter,
  subQuarters,
  lastDayOfQuarter,
  subYears,
} from 'date-fns'

import dayjs from 'dayjs'
import customParseFormat from 'dayjs/plugin/customParseFormat'
dayjs.extend(customParseFormat)

const minDate = new Date('1990-01-01T00:00:00.000')
const maxDate = new Date('2040-03-01T00:00:00.000')

const DateRangePickerDay = styled(MuiDateRangePickerDay)(
  ({ theme, isHighlighting, isStartOfHighlighting, isEndOfHighlighting }) => ({
    ...(isHighlighting && {
      borderRadius: 0,
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      '&:hover, &:focus': {
        backgroundColor: theme.palette.primary.dark,
      },
    }),
    ...(isStartOfHighlighting && {
      borderTopLeftRadius: '50%',
      borderBottomLeftRadius: '50%',
    }),
    ...(isEndOfHighlighting && {
      borderTopRightRadius: '50%',
      borderBottomRightRadius: '50%',
    }),
  }),
)

export const DateDefine = {
  startOfWeek: startOfWeek(new Date()),
  endOfWeek: endOfWeek(new Date()),
  startOfLastWeek: startOfWeek(addDays(new Date(), -7)),
  endOfLastWeek: endOfWeek(addDays(new Date(), -7)),
  startOfLast7Days: startOfDay(addDays(new Date(), -7)),
  startOfLast30Days: startOfDay(addDays(new Date(), -30)),
  startOfToday: startOfDay(new Date()),
  endOfToday: endOfDay(new Date()),
  startOfYesterday: startOfDay(addDays(new Date(), -1)),
  endOfYesterday: endOfDay(addDays(new Date(), -1)),
  startOfMonth: startOfMonth(new Date()),
  endOfMonth: endOfMonth(new Date()),
  startOfLastMonth: startOfMonth(addMonths(new Date(), -1)),
  endOfLastMonth: endOfMonth(addMonths(new Date(), -1)),
  startOfYear: startOfYear(new Date()),
  endOfYear: endOfYear(new Date()),
  startOfLastYear: startOfYear(subYears(new Date(), 1)),
  endOfLastYear: endOfYear(subYears(new Date(), 1)),
  startOf3MonthAgo: startOfMonth(addMonths(new Date(), -3)),
  startOfBudgetYear: addYears(new Date(getYear(new Date()), 9, 1), -1),
  endOfBudgetYear: new Date(getYear(new Date()), 8, 30),
  lastQuarterStartDate: startOfQuarter(subQuarters(new Date(), 1)),
  lastQuarterEndDate: lastDayOfQuarter(subQuarters(new Date(), 1)),
}

const useStyles = makeStyles((theme) => ({
  btnBgGray: {
    '&.MuiIconButton-root': { backgroundColor: '#e7e5e5' },
  },
}))

const DateRangePickerCustom = ({
  dateAdapter,
  locale,
  value,
  onChange,
  placeholder,
  formatDisplay,
  maskSign,
  isClearable = true,
  isError,
  ...props
}) => {
  const retryRef = useRef(0)
  const classes = useStyles()
  let formatDsp = formatDisplay ?? 'DD/MM/YYYY'
  let maskInput =
    formatDisplay && maskSign
      ? formatDsp.split('').reduce((items, item) => {
          if (item === maskSign) {
            items += maskSign
          } else {
            items += 9
          }
          return items
        }, '')
      : '99/99/9999'

  const [dateValue, setDateValue] = useState(value ? value : [null, null])
  const [dateText, setDateText] = useState([
    value && value[0]
      ? dayjs(value[0]).add(543, 'year').format(formatDsp)
      : '',
    value && value[1]
      ? dayjs(value[1]).add(543, 'year').format(formatDsp)
      : '',
  ])

  const renderWeekPickerDay = (date, dateRangePickerDayProps) => {
    return <DateRangePickerDay {...dateRangePickerDayProps} />
  }

  const [defaultDateMonth, setDefaultDateMonth] = useState(new Date())

  const [action, setAction] = useState('range')

  const [calendarView, setCalendarView] = useState('day')
  const [showPopover, setShowPopover] = useState(false)

  //// scroll to current year /////

  const scrollToCurrentYear = () => {
    retryRef.current = 0

    const tryScroll = () => {
      const selectors = [
        '.MuiYearCalendar-root button',
        '.MuiPickersYear-root button',
        '[role="gridcell"] button',
        '.MuiCalendarPicker-root button',
        'button',
      ]
      const possibleButtons = Array.from(
        document.querySelectorAll(selectors.join(',')),
      )

      if (!possibleButtons.length) {
        retry()
        return
      }

      const numericButtons = possibleButtons.filter((btn) => {
        const t = btn.textContent?.trim()
        return !!t && /^\d{3,4}$/.test(t)
      })

      if (!numericButtons.length) {
        retry()
        return
      }

      const ceYear = new Date().getFullYear()
      const beYear = ceYear + 543

      const showsBE = numericButtons.some(
        (b) => b.textContent.trim() === String(beYear),
      )
      const targetText = showsBE ? String(beYear) : String(ceYear)

      const targetBtn = numericButtons.find(
        (b) => b.textContent.trim() === targetText,
      )

      if (!targetBtn) {
        const fuzzy = numericButtons.find(
          (b) => Math.abs(Number(b.textContent.trim()) - ceYear) <= 543,
        )
        if (fuzzy) {
          scrollIntoContainer(fuzzy)
          return
        }
        retry()
        return
      }

      scrollIntoContainer(targetBtn)
    }

    const retry = () => {
      retryRef.current += 1
      if (retryRef.current > 6) return

      setTimeout(() => requestAnimationFrame(tryScroll), 80)
    }

    const scrollIntoContainer = (targetBtn) => {
      let container = targetBtn.parentElement
      while (container && container !== document.body) {
        const style = window.getComputedStyle(container)
        if (/(auto|scroll)/.test(style.overflowY)) break
        container = container.parentElement
      }

      if (container && container !== document.body) {
        const offset =
          targetBtn.offsetTop -
          container.offsetTop -
          container.clientHeight / 2 +
          targetBtn.clientHeight / 2
        container.scrollTo({ top: offset, behavior: 'smooth' })
      } else {
        targetBtn.scrollIntoView({ block: 'center', behavior: 'smooth' })
      }

      try {
        targetBtn.focus()
      } catch (e) {}
    }

    requestAnimationFrame(tryScroll)
  }
  useEffect(() => {
    if (calendarView === 'year') {
      scrollToCurrentYear()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calendarView])
  ///
  useEffect(() => {
    const nextValue = value || [null, null]

    setDateValue(nextValue)
    setDateText([
      nextValue && nextValue[0]
        ? dayjs(nextValue[0]).add(543, 'year').format(formatDsp)
        : '',
      nextValue && nextValue[1]
        ? dayjs(nextValue[1]).add(543, 'year').format(formatDsp)
        : '',
    ])
  }, [value, formatDsp])

  const checkStartEndDate = (start, end) => {
    let startValue = dateValue[0] ? dateValue[0].setHours(0, 0, 0, 0) : null
    let endValue = dateValue[1] ? dateValue[1].setHours(0, 0, 0, 0) : null

    let startSelect = start.setHours(0, 0, 0, 0)
    let endSelect = end.setHours(0, 0, 0, 0)

    return startValue === startSelect && endValue === endSelect
  }

  const parseStringDate = (dateString) => {
    return dayjs(dateString).toDate()
  }

  const converteDate = (data) => {
    if (data) {
      const date = dayjs(data, formatDsp)
      if (date.isValid()) {
        const ceYear = date.year() - 543
        const ceDate = `${ceYear}${date.format('-MM-DD')}`
        return parseStringDate(ceDate)
      } else {
        return ''
      }
    } else {
      return ''
    }
  }

  const handleChangeDateText = (e, actionInput) => {
    let { value } = e.target

    let newDateText = [...dateText]
    if (actionInput === 'start') {
      newDateText[0] = value
    } else if (actionInput === 'end') {
      newDateText[1] = value
    }

    setDateText(newDateText)

    let newDate = converteDate(value)

    if (newDate) {
      let dateStartFormat = dayjs(newDate[0])
        .add(543, 'year')
        .format('YYYY-MM-DD')
      let dateEndFormat = dayjs(newDate[1])
        .add(543, 'year')
        .format('YYYY-MM-DD')
      let newDateValue = [...dateValue]

      if (actionInput === 'start') {
        newDateValue[0] = newDate
      } else if (actionInput === 'end') {
        newDateValue[1] = newDate
      }

      setDateValue(newDateValue)

      if (onChange) {
        onChange({
          value: newDateValue,
          format: [dateStartFormat, dateEndFormat],
        })
      }
    }
  }

  const handleChangeDate = (date, dismiss = true) => {
    let dateStartFormat = date[0]
      ? dayjs(date[0]).add(543, 'year').format('YYYY-MM-DD')
      : null
    let dateEndFormat = date[1]
      ? dayjs(date[1]).add(543, 'year').format('YYYY-MM-DD')
      : null
    let dateStartDisplay = date[0]
      ? dayjs(date[0]).add(543, 'year').format(formatDsp)
      : ''
    let dateEndDisplay = date[1]
      ? dayjs(date[1]).add(543, 'year').format(formatDsp)
      : ''

    setDateValue(date)
    setDateText([dateStartDisplay, dateEndDisplay])

    if (onChange) {
      onChange({ value: date, format: [dateStartFormat, dateEndFormat] })
    }

    setShowPopover(!dismiss)
  }

  const clearDate = (actionInput) => {
    let newDateValue = [...dateValue]
    let newDateText = [...dateText]

    if (actionInput === 'start') {
      newDateValue[0] = null
      newDateText[0] = ''
    } else if (actionInput === 'end') {
      newDateValue[1] = null
      newDateText[1] = ''
    }

    setDateValue(newDateValue)
    setDateText(newDateText)

    if (onChange) {
      onChange({ value: newDateValue, format: newDateText })
    }
  }

  const getKey = useMemo(() => {
    // for re-render StaticDateRangePicker
    if (dateValue[1]) {
      return new Date().getTime()
    }
  }, [dateValue])
  const showDefaultOptions = props.showDefaultOptions !== false

  const popover = (
    <Popover className="popover-calendar">
      <Popover.Content>
        <Row>
          <Col>
            <ul class="list-group list-group-flush">
              {props.renderOptionTemplate &&
                props.renderOptionTemplate(checkStartEndDate, handleChangeDate)}
              {showDefaultOptions && (
                <>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfToday,
                        DateDefine.endOfToday,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfToday,
                        DateDefine.endOfToday,
                      ])
                    }
                  >
                    วันนี้
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfLast7Days,
                        DateDefine.endOfToday,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfLast7Days,
                        DateDefine.endOfToday,
                      ])
                    }
                  >
                    7 วันที่แล้ว
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfLast30Days,
                        DateDefine.endOfToday,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfLast30Days,
                        DateDefine.endOfToday,
                      ])
                    }
                  >
                    30 วันที่แล้ว
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfLastWeek,
                        DateDefine.endOfLastWeek,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfLastWeek,
                        DateDefine.endOfLastWeek,
                      ])
                    }
                  >
                    อาทิตย์ที่แล้ว
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfLastMonth,
                        DateDefine.endOfLastMonth,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfLastMonth,
                        DateDefine.endOfLastMonth,
                      ])
                    }
                  >
                    เดือนที่แล้ว
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfMonth,
                        DateDefine.endOfMonth,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfMonth,
                        DateDefine.endOfMonth,
                      ])
                    }
                  >
                    เดือนนี้
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOf3MonthAgo,
                        DateDefine.endOfLastMonth,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOf3MonthAgo,
                        DateDefine.endOfLastMonth,
                      ])
                    }
                  >
                    3 เดือนที่แล้ว
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.lastQuarterStartDate,
                        DateDefine.lastQuarterEndDate,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.lastQuarterStartDate,
                        DateDefine.lastQuarterEndDate,
                      ])
                    }
                  >
                    ไตรมาสที่แล้ว
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfBudgetYear,
                        DateDefine.endOfBudgetYear,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfBudgetYear,
                        DateDefine.endOfBudgetYear,
                      ])
                    }
                  >
                    ทั้งปีงบประมาณนี้
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfYear,
                        DateDefine.endOfYear,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfYear,
                        DateDefine.endOfYear,
                      ])
                    }
                  >
                    ปีนี้
                  </li>
                  <li
                    className={classNames('list-group-item', {
                      'text-primary font-weight-bold': checkStartEndDate(
                        DateDefine.startOfLastYear,
                        DateDefine.endOfLastYear,
                      ),
                    })}
                    onClick={() =>
                      handleChangeDate([
                        DateDefine.startOfLastYear,
                        DateDefine.endOfLastYear,
                      ])
                    }
                  >
                    ปีที่แล้ว
                  </li>
                </>
              )}
            </ul>
          </Col>
          <Col>
            <div className="text-center">
              <Row className="mt-2">
                <Col>
                  <FormGroup className="text-left">
                    <FormLabel>เริ่มต้น</FormLabel>
                    <div className="d-flex">
                      <InputMask
                        className={classNames('form-control')}
                        maskChar={null}
                        value={
                          dateValue[0]
                            ? dayjs(dateValue[0])
                                .add(543, 'year')
                                .format(formatDsp)
                            : ''
                        }
                      />
                      <IconButton
                        className={action === 'start' && classes.btnBgGray}
                        onClick={() => {
                          if (action !== 'start') {
                            setAction('start')
                          } else {
                            setAction('range')
                          }
                        }}
                      >
                        <TodayIcon />
                      </IconButton>
                    </div>
                  </FormGroup>
                </Col>
                <Col>
                  <FormGroup className="text-left">
                    <FormLabel>สิ้นสุด</FormLabel>

                    <div className="d-flex">
                      <InputMask
                        className={classNames('form-control')}
                        maskChar={null}
                        value={
                          dateValue[1]
                            ? dayjs(dateValue[1])
                                .add(543, 'year')
                                .format(formatDsp)
                            : ''
                        }
                      />
                      <IconButton
                        className={action === 'end' && classes.btnBgGray}
                        onClick={() => {
                          if (action !== 'end') {
                            setAction('end')
                          } else {
                            setAction('range')
                          }
                        }}
                      >
                        <TodayIcon />
                      </IconButton>
                    </div>
                  </FormGroup>
                </Col>
              </Row>
            </div>
            <div className="text-center">
              <LocalizationProvider
                dateAdapter={dateAdapter ?? OverwriteFnsBE}
                locale={locale ?? thLocale}
              >
                {(action === 'start' || action === 'end') && (
                  <CalendarPicker
                    openTo="year"
                    view={calendarView}
                    onViewChange={(view) => setCalendarView(view)}
                    views={['year', 'month', 'day']}
                    date={
                      action === 'start'
                        ? dateValue[0]
                        : action === 'end'
                        ? dateValue[1]
                        : null
                    }
                    minDate={minDate}
                    maxDate={maxDate}
                    onChange={(newDate) => {
                      console.log('newDate ', newDate)
                      if (action === 'start') {
                        let newValue = [...dateValue]
                        newValue[0] = newDate

                        if (newDate > newValue[1]) {
                          newValue[1] = null
                        }
                        handleChangeDate(newValue, false)
                      } else if (action === 'end') {
                        let newValue = [...dateValue]
                        newValue[1] = newDate

                        if (newDate < newValue[0]) {
                          newValue[0] = null
                        }
                        handleChangeDate(newValue)
                      }

                      if (calendarView === 'year') {
                        setCalendarView('month')
                      } else if (calendarView === 'month') {
                        setCalendarView('day')
                        setAction('range')
                      }
                      if (calendarView !== 'year') {
                        setAction('range')
                      }
                    }}
                  />
                )}

                {action === 'range' && (
                  // <StaticDateRangePicker
                  //   onMonthChange={(e) => setDefaultDateMonth(e)}
                  //   currentCalendarMonth={defaultDateMonth}
                  //   // defaultCalendarMonth={dateMonth}
                  //   calendars={1}
                  //   displayStaticWrapperAs="desktop"
                  //   label="date range"
                  //   value={dateValue}
                  //   onChange={(newValue) => handleChangeDate(newValue)}
                  //   renderDay={renderWeekPickerDay}
                  //   minDate={minDate}
                  //   maxDate={maxDate}
                  //   renderInput={(startProps, endProps) => (
                  //     <React.Fragment>
                  //       <TextField {...startProps} />
                  //       <Box sx={{ mx: 2 }}> to </Box>
                  //       <TextField {...endProps} />
                  //     </React.Fragment>
                  //   )}
                  // />
                  <StaticDateRangePicker
                    key={getKey}
                    onMonthChange={(e) => {
                      // setDefaultDateMonth(e)
                      setDefaultDateMonth([dayjs(), dayjs()])
                    }}
                    currentCalendarMonth={defaultDateMonth}
                    // defaultCalendarMonth={dateMonth}
                    calendars={1}
                    displayStaticWrapperAs="desktop"
                    label="date range"
                    value={dateValue}
                    onChange={(newValue) => handleChangeDate(newValue, false)}
                    renderDay={renderWeekPickerDay}
                    minDate={minDate}
                    maxDate={maxDate}
                    renderInput={(startProps, endProps) => (
                      <React.Fragment>
                        <TextField {...startProps} />
                        <Box sx={{ mx: 2 }}> to </Box>
                        <TextField {...endProps} />
                      </React.Fragment>
                    )}
                  />
                )}
              </LocalizationProvider>
            </div>
          </Col>
        </Row>
      </Popover.Content>
    </Popover>
  )

  return (
    <>
      <Row>
        <Col className="my-auto">
          <Row>
            <Col>
              <div className="position-relative">
                <InputMask
                  className={classNames('form-control', {
                    'input-date pr': isClearable,
                    'is-invalid': isError,
                  })}
                  value={dateText[0]}
                  placeholder={placeholder ?? formatDsp}
                  mask={maskInput}
                  maskChar={null}
                  onChange={(e) => handleChangeDateText(e, 'start')}
                />
                {isClearable && dateText && dateText[0] && (
                  <button
                    className="clear-btn"
                    onClick={() => clearDate('start')}
                  >
                    <i
                      className={classNames('fas fa-times clearable-btn', {
                        error: isError,
                      })}
                    />
                  </button>
                )}
              </div>
            </Col>

            <Col>
              <div className="position-relative">
                <InputMask
                  className={classNames('form-control', {
                    'input-date pr': isClearable,
                    'is-invalid': isError,
                  })}
                  value={dateText[1]}
                  placeholder={placeholder ?? formatDsp}
                  mask={maskInput}
                  maskChar={null}
                  onChange={(e) => handleChangeDateText(e, 'end')}
                />

                {isClearable && dateText && dateText[1] && (
                  <button
                    className="clear-btn"
                    onClick={() => clearDate('end')}
                  >
                    <i
                      className={classNames('fas fa-times clearable-btn', {
                        error: isError,
                      })}
                    />
                  </button>
                )}
              </div>
            </Col>
          </Row>
        </Col>

        <OverlayTrigger
          trigger="click"
          placement="bottom"
          overlay={popover}
          rootClose
          show={showPopover}
          onToggle={(nextShow) => setShowPopover(nextShow)}
        >
          <IconButton onClick={() => setShowPopover(!showPopover)}>
            <TodayIcon />
          </IconButton>
        </OverlayTrigger>
      </Row>
    </>
  )
}

export default DateRangePickerCustom
